import {useTranslation} from '../../utils/useTranslation'
import {theme} from '../../theme'
import {EShift} from '../../types/app.type'
import {useCurrentShift, useUIActions} from '../../store'
import {useFilterTracking} from '../../hooks/filterTracking.hooks'
import ButtonGroup from '../buttonGoup'
import IconButton from '../iconButton'

interface ShiftFilterBaseProps {
  value: EShift
  onChange: (shift: EShift) => void
}

/**
 * Presentational day / night / both shift switch. Selection state comes from
 * the caller — the store-connected default export for the project views, the
 * dashboard's own filter store for the network dashboard.
 */
export function ShiftFilterBase({value, onChange}: ShiftFilterBaseProps) {
  const {t} = useTranslation()

  const colorFor = (shift: EShift) =>
    value === shift ? theme.palette.primary.main : theme.palette.text.primary

  return (
    <ButtonGroup>
      <IconButton
        icon='shift_day'
        iconSize='24px'
        iconColor={colorFor('day')}
        active={value === 'day'}
        onClick={() => onChange('day')}
        tooltip={t('tooltip:day')}
      />
      <IconButton
        icon='shift_night'
        iconSize='24px'
        iconColor={colorFor('night')}
        active={value === 'night'}
        onClick={() => onChange('night')}
        tooltip={t('tooltip:night')}
      />
      <IconButton
        icon='shift_both'
        iconSize='24px'
        iconColor={colorFor('both')}
        active={value === 'both'}
        onClick={() => onChange('both')}
        tooltip={t('tooltip:daynight')}
      />
    </ButtonGroup>
  )
}

/**
 * Store-connected wrapper: reads and writes the shift from the UI store and
 * reports its own usage to Amplitude. Behavior identical to the pre-refactor
 * component.
 */
export default function ShiftFilter() {
  const currentShift = useCurrentShift()
  const {setCurrentShift} = useUIActions()
  const trackFilter = useFilterTracking()

  const onChange = (shift: EShift) => {
    setCurrentShift(shift)
    trackFilter('shift')
  }

  return <ShiftFilterBase value={currentShift} onChange={onChange} />
}
